"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ruleVocabularySchema = exports.lineItemRuleSchema = exports.ruleConditionSchema = exports.RULE_IN_MAX = exports.isListOp = exports.RULE_OPS = exports.RULE_REMOTE_FIELDS = exports.RULE_SET_FIELDS = exports.RULE_FIELDS = exports.RULE_MATCHES = void 0;
exports.matchesRule = matchesRule;
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C26 · The rule engine's matcher. One task, one rule, yes or no.           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `2fazis.main.jsx` 322–343 (`liMatchOne`, `liMembers`), with its vocabulary intact and its
 * looseness removed.
 *
 * ⚠️⚠️ **IN `shared` BECAUSE BOTH SIDES RUN IT, AND FOR DIFFERENT REASONS.** The server runs it to
 * MATERIALISE — a rule match becomes `tasks.line_item_id` (migration `0055` argues why at length) —
 * and the client runs it to PREVIEW, so the rule editor can show what a condition would catch
 * before anybody saves it. Two implementations of *"does this task match"* is a preview that lies.
 *
 * ⚠️ **Pure, and unit-tested**: CLAUDE.md §4 names "anything with a predicate", and this is nothing
 * but predicate.
 */
exports.RULE_MATCHES = ['all', 'any'];
/**
 * The mockup's `LI_FIELDS`, named for what they are — and three that only a tracker can answer.
 *
 * ⚠️⚠️ **`component`, `fix_version` AND `epic` ARE ADDITIONS (rule 8), RECORDED AS `decisions.md`
 * P2-30.** They read what Jira says about a synced issue (`0160`), so a task with no Jira link has
 * none of them and only `ne` can match it. `jira.md` §3.2 planned them; the legacy migration showed
 * they carry most of the hours its contracts were billed by.
 */
exports.RULE_FIELDS = [
    /**
     * ⚠️ **J-9 · kept for stored rows, never offered.** Since L5 every rule sits on a targeted line
     * item, and there the Forecastify project is what routing decides — an inbound issue has none
     * when the rule is asked, so `project` can never match it. The editor hides it; the matcher still
     * reads a condition written before 28 Oct 2026, so it can be seen and removed.
     */
    'project',
    'status',
    'priority',
    'assignee',
    'tag',
    'key',
    'title',
    'component',
    'fix_version',
    'epic',
];
/** The fields a task may hold several of, where `eq` means *has* rather than *is*. */
exports.RULE_SET_FIELDS = new Set(['tag', 'component', 'fix_version']);
/** The fields whose value is a Jira fact rather than one of ours — `0160`. */
exports.RULE_REMOTE_FIELDS = new Set([
    'component',
    'fix_version',
    'epic',
]);
/**
 * ⚠️ **`in` is an addition too (P2-30): *any one of these*.** `all` / `any` is one level deep, and
 * the legacy rule most hours ran on is `component IN (a, b) AND fixVersion IN (c, d)` — an OR inside
 * each field and an AND across them, which a flat list of single-valued conditions cannot express.
 */
exports.RULE_OPS = ['eq', 'ne', 'contains', 'in', 'not_in'];
/**
 * ⚠️ 5 Nov 2026 · **`not_in` is `in`'s mirror (RN-1, `0259`): *none of these*.** Every condition on
 * a field with a list of values is a multiselect now, and *is not one of* took a `ne` per value.
 * Both keep their candidates in `values`; this says which operators do.
 */
const isListOp = (op) => op === 'in' || op === 'not_in';
exports.isListOp = isListOp;
/** How many candidates one `in` condition may list. One live legacy target named 38 components. */
exports.RULE_IN_MAX = 100;
exports.ruleConditionSchema = zod_1.z.object({
    field: zod_1.z.enum(exports.RULE_FIELDS),
    op: zod_1.z.enum(exports.RULE_OPS),
    /**
     * ⚠️ **A string for every field, including the id-valued ones.** `project` and `assignee` carry a
     * uuid as text; `status` and `priority` a vocabulary id; the rest what somebody typed. See `0055`
     * on why this is not six typed columns.
     */
    value: zod_1.z.string().max(200),
    /**
     * ⚠️ **The candidates of an `in`, and absent or empty for every other operator** — `0160`'s
     * `value_list`. Optional, so every condition written before `in` existed is still a valid one and
     * reads exactly as it did. The service keeps the two apart on write (`value` for one candidate,
     * `values` for a list) rather than refusing a condition somebody is halfway through switching.
     */
    values: zod_1.z.array(zod_1.z.string().max(200)).max(exports.RULE_IN_MAX).optional(),
});
exports.lineItemRuleSchema = zod_1.z.object({
    match: zod_1.z.enum(exports.RULE_MATCHES),
    /**
     * ⚠️ **Capped at 12.** A rule nobody can read is a rule nobody can audit, and the engine
     * re-evaluates it on every task write in the project — twelve is past the point where a person
     * would reach for a saved filter instead.
     */
    conditions: zod_1.z.array(exports.ruleConditionSchema).max(12),
});
/**
 * What the rule editor's pickers offer for the three Jira fields of one line item's project —
 * `GET /line-items/:id/rule-vocabulary`.
 *
 * ⚠️ **What the project's synced issues actually carry, not the tracker's whole catalogue.** A
 * component no issue has is a condition that matches nothing; offering only what is present keeps
 * the picker honest and costs no request to Jira. Values are remote ids; names are for display.
 */
const vocabularyEntrySchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    /** How many of the project's tasks carry it — so the picker can say what a choice would catch. */
    taskCount: zod_1.z.number().int().nonnegative(),
});
exports.ruleVocabularySchema = zod_1.z.object({
    components: zod_1.z.array(vocabularyEntrySchema),
    fixVersions: zod_1.z.array(vocabularyEntrySchema),
    /** An epic's `name` is its key and title, `HSO-12 · Search` — what a person recognises it by. */
    epics: zod_1.z.array(vocabularyEntrySchema),
});
/**
 * Does this task match this rule?
 *
 * ⚠️⚠️ **A RULE WITH NO CONDITIONS MATCHES NOTHING, IN BOTH MODES.** `Array.every` on an empty array
 * is `true`, which would make an empty `all` rule claim every task in the workspace the moment
 * somebody switched a line item to rule mode and had not typed anything yet. The mockup dodges this
 * by falling back to manual membership when the list is empty; being explicit is better — an empty
 * rule is an unfinished rule.
 */
function matchesRule(task, rule) {
    if (rule.conditions.length === 0)
        return false;
    const results = rule.conditions.map((condition) => matchesCondition(task, condition));
    return rule.match === 'any' ? results.some(Boolean) : results.every(Boolean);
}
/**
 * One condition.
 *
 * ⚠️⚠️ **`tag`, `component` AND `fix_version` ARE SETS AND THE REST ARE SCALARS, WHICH CHANGES WHAT
 * `ne` MEANS.** *"tag is not urgent"* has to mean *"carries no tag called urgent"*, not *"the joined
 * tag string differs from urgent"* — the mockup gets this right for tags, and Jira's components and
 * fix versions are lists in exactly the same way. Everything else compares one value.
 *
 * ⚠️ **`in` is `eq` against each candidate, any one of which is enough** — on a set, *has any of
 * them*; on a scalar, *is one of them*. Like `eq`, an empty candidate list matches nothing.
 *
 * ⚠️ **Case-insensitive throughout**, including on ids: a uuid compared case-sensitively is fine,
 * and a tag compared case-sensitively is a rule that silently misses `Urgent`.
 *
 * ⚠️ **An empty `value` never matches on `eq`.** A half-typed condition would otherwise claim every
 * task whose assignee is unset — an empty string equals an empty string — which is the wrong answer
 * arrived at honestly, and the reason `matchesRule` refuses an empty condition list too.
 */
function matchesCondition(task, condition) {
    const needle = condition.value.trim().toLowerCase();
    const candidates = (condition.values ?? [])
        .map((one) => one.trim().toLowerCase())
        .filter((one) => one !== '');
    if (exports.RULE_SET_FIELDS.has(condition.field)) {
        const members = setOf(task, condition.field).map((one) => one.toLowerCase());
        if (condition.op === 'in')
            return candidates.some((one) => members.includes(one));
        if (condition.op === 'not_in') {
            return candidates.length > 0 && !candidates.some((one) => members.includes(one));
        }
        const has = members.includes(needle);
        if (condition.op === 'eq')
            return needle !== '' && has;
        if (condition.op === 'ne')
            return needle === '' || !has;
        return needle !== '' && members.some((one) => one.includes(needle));
    }
    const value = scalar(task, condition.field).toLowerCase();
    if (condition.op === 'in')
        return value !== '' && candidates.includes(value);
    /* ⚠️ RN-1 · an EMPTY `not_in` matches nothing, like an empty `in` — an unfinished condition claims
       nothing. With candidates, a task with no value at all is none of them. */
    if (condition.op === 'not_in')
        return candidates.length > 0 && !candidates.includes(value);
    if (condition.op === 'eq')
        return needle !== '' && value === needle;
    /* ⚠️ `ne` against an empty needle is vacuously true rather than false — "not blank" is a
       question somebody could sensibly ask, and it is the only reading that is not a trap. */
    if (condition.op === 'ne')
        return value !== needle;
    return needle !== '' && value.includes(needle);
}
function setOf(task, field) {
    if (field === 'component')
        return task.components;
    if (field === 'fix_version')
        return task.fixVersions;
    return task.tags;
}
function scalar(task, field) {
    if (field === 'epic')
        return task.epicId ?? '';
    if (field === 'project')
        return task.projectId ?? '';
    if (field === 'status')
        return task.statusId ?? '';
    if (field === 'priority')
        return task.priorityId ?? '';
    if (field === 'assignee')
        return task.assigneeMemberId ?? '';
    if (field === 'key')
        return task.key;
    return task.title;
}
//# sourceMappingURL=rules.js.map